import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import "./Result.css";
import ResultModalLayout from '@/components/ResultModalLayout';

// Helper function to map card string to image path
const getCardImage = (cardString) => {
  if (!cardString) return null;
  const match = cardString.match(/^([A-Z0-9]+)([A-Z])\2?$/);
  if (match) {
    const value = match[1];
    const suit = match[2];
    const cleanCard = `${value}${suit}`;
    return `/assets/img/cards/${cleanCard}.png`;
  }
  return `/assets/img/cards/${cardString}.png`;
};

// Helper function to parse card string for TeenJoker
// Format: "KSS,2SS,4SS,9DD,8DD,6CC,KCC" - 7 cards total
// First 3 cards for Player A, next 3 cards for Player B, last 1 card is Joker
const parseCards = (cardString) => {
  if (!cardString) return { playerA: [], playerB: [], joker: null };
  
  const cards = cardString.split(',');
  const playerACards = cards.slice(0, 3).map(card => getCardImage(card.trim()));
  const playerBCards = cards.slice(3, 6).map(card => getCardImage(card.trim()));
  const jokerCard = cards.length > 6 ? getCardImage(cards[6].trim()) : null;
  
  return { playerA: playerACards, playerB: playerBCards, joker: jokerCard };
};

// Helper function to parse rdesc for TeenJoker
// Format: "Player B#Odd#Black#Spade"
const parseRdesc = (rdesc) => {
  if (!rdesc) return {};
  
  const parts = rdesc.split('#');
  const winner = parts[0] || "";
  const jokerEvenOdd = parts[1] || "";
  const jokerRedBlack = parts[2] || "";
  const jokerSuit = parts[3] || "";
  
  return { 
    winner,
    jokerEvenOdd,
    jokerRedBlack,
    jokerSuit
  };
};

const ResultTeenJoker = ({ 
  data = [], 
  gameData = {},
}) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [lastResults, setLastResults] = useState([]);
  const [detailResult, setDetailResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch last results on mount
  useEffect(() => {
    const fetchLastResults = async () => {
      try {
        const response = await getLastResults('teenjoker');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    };
    
    fetchLastResults();
  }, []);

  const handleResultClick = useCallback(async (result, index) => {
    setSelectedResult(result);
    setLoading(true);
    
    if (lastResults.length === 0) {
      try {
        const response = await getLastResults('teenjoker');
        if (response?.data?.data?.res) {
          const mappedResults = response.data.data.res.map(item => ({
            mid: item.mid,
            win: item.win === "1" ? "A" : item.win === "2" ? "B" : item.win
          }));
          setLastResults(mappedResults);
        }
      } catch (error) {
        console.error('Error fetching last results:', error);
      }
    }
    
    let mid = null;
    if (index < lastResults.length) {
      mid = lastResults[index]?.mid;
    } else if (index < data.length + (gameData?.lrs?.length || 0)) {
      mid = gameData?.data?.data?.mid || gameData?.mid;
    }
    
    if (mid && detailResult?.rid !== mid.toString()) {
      try {
        const response = await getDetailResults('teenjoker', mid.toString());
        if (response?.data?.data?.t1) {
          setDetailResult(response.data.data.t1);
        }
      } catch (error) {
        console.error('Error fetching detail results:', error);
      }
    }
    
    setLoading(false);
    setShowModal(true);
  }, [lastResults, data, gameData, detailResult]);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedResult(null);
    setDetailResult(null);
  };

  // Format modal data - memoized to prevent re-renders
  const modalData = useMemo(() => {
    if (!selectedResult || !showModal) return null;

    const cards = detailResult?.card ? parseCards(detailResult.card) : { 
      playerA: [], 
      playerB: [],
      joker: null
    };
    
    const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};
    const winner = detailResult?.winnat || (selectedResult === "A" ? "Player A" : selectedResult === "B" ? "Player B" : "");
    
    const winValue = detailResult?.win;
    const isPlayerAWinner = winValue === "1" || winner === "Player A" || selectedResult === "A";
    const isPlayerBWinner = winValue === "2" || winner === "Player B" || selectedResult === "B";

    return {
      roundId: detailResult?.rid || gameData?.data?.data?.mid || gameData?.mid || "157250124095658",
      matchTime: detailResult?.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
      playerA: {
        cards: cards.playerA,
        isWinner: isPlayerAWinner,
      },
      playerB: {
        cards: cards.playerB,
        isWinner: isPlayerBWinner,
      },
      joker: {
        card: cards.joker,
        evenOdd: rdescData.jokerEvenOdd || "",
        redBlack: rdescData.jokerRedBlack || "",
        suit: rdescData.jokerSuit || "",
      },
      resultDetails: {
        winner: winner,
      },
    };
  }, [selectedResult, detailResult, gameData, showModal]);

  // Combine API results with fallback data
  const allResults = lastResults.length > 0 
    ? lastResults.map(r => r.win)
    : [...data, ...(gameData?.lrs || [])];

  return (
    <>
      <div className="result-row">
        {allResults.map((res, index) => (
          <div
            key={index}
            className={`result-circle ${res === "A" ? "yes" : "no"}`}
            onClick={() => handleResultClick(res, index)}
            style={{ cursor: 'pointer' }}
          >
            {res}
          </div>
        ))}
      </div>

      {/* Modal */}
      {modalData && (
        <ResultModalLayout
          show={showModal}
          onHide={handleCloseModal}
          title="Teenpatti Joker Result"
          roundId={modalData.roundId}
          matchTime={modalData.matchTime}
          loading={loading}
        >
          <div>
            {/* Round ID */}
            <h6 className="text-right round-id" style={{ marginBottom: '1rem' }}>
              <b>Round Id:</b> {modalData.roundId}
            </h6>

            {/* Joker Card Section */}
            {modalData.joker?.card && (
              <div className="text-center" style={{ marginBottom: '1.5rem' }}>
                <h5 style={{ marginBottom: '0.5rem', fontSize: '16px' }}>Joker Card</h5>
                <div style={{ display: 'inline-block' }}>
                  <img
                    src={modalData.joker.card}
                    alt="Joker Card"
                    onError={(e) => {
                      const cardName = modalData.joker.card?.split('/').pop();
                      const fallbackPath = `/assets/img/card-results/${cardName}`;
                      if (e.target.src !== fallbackPath) {
                        e.target.src = fallbackPath;
                      } else {
                        e.target.style.display = 'none';
                      }
                    }}
                    style={{ width: '40px', height: 'auto', margin: '0 auto' }}
                  />
                </div>
                {(modalData.joker.evenOdd || modalData.joker.redBlack || modalData.joker.suit) && (
                  <div style={{ marginTop: '0.5rem', fontSize: '12px', color: '#666' }}>
                    {modalData.joker.evenOdd && <span>{modalData.joker.evenOdd} </span>}
                    {modalData.joker.redBlack && <span>{modalData.joker.redBlack} </span>}
                    {modalData.joker.suit && <span>{modalData.joker.suit}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Player Cards Section */}
            <div className="row">
              {/* Player A */}
              <div className="col-md-6">
                <h4 className="text-center">Player A</h4>
                <div className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {modalData.playerA?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player A Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                        style={{ margin: '0', maxWidth: '40px', height: 'auto' }}
                      />
                    )
                  ))}
                </div>
                {modalData.playerA?.isWinner && (
                  <div className="text-center m-t-10">
                    <label className="winner-label bg-success">
                      <i className="fas fa-trophy m-r-5"></i>Winner
                    </label>
                  </div>
                )}
              </div>
              {/* Player B */}
              <div className="col-md-6">
                <h4 className="text-center">Player B</h4>
                <div className="text-center" style={{ display: 'flex', justifyContent: 'center', gap: '4px', flexWrap: 'wrap' }}>
                  {modalData.playerB?.cards?.map((card, index) => (
                    card && (
                      <img
                        key={index}
                        src={card}
                        alt={`Player B Card ${index + 1}`}
                        onError={(e) => {
                          const cardName = card.split('/').pop();
                          const fallbackPath = `/assets/img/card-results/${cardName}`;
                          if (e.target.src !== fallbackPath) {
                            e.target.src = fallbackPath;
                          } else {
                            e.target.style.display = 'none';
                          }
                        }}
                        style={{ margin: '0', maxWidth: '40px', height: 'auto' }}
                      />
                    )
                  ))}
                </div>
                {modalData.playerB?.isWinner && (
                  <div className="text-center m-t-10">
                    <label className="winner-label bg-success">
                      <i className="fas fa-trophy m-r-5"></i>Winner
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </ResultModalLayout>
      )}
    </>
  );
};

export default ResultTeenJoker;

